import React from "react";
import { Typography, Card } from "antd";
import { InfoCircleOutlined } from '@ant-design/icons';
import { ResponsiveLine } from '@nivo/line';
import CustomTooltip from "../Components/Modals/CustomTooltip";
import EmptyCard from "../EmptyCard";
import LoadingCard from "../Components/Cards/LoadingCard";
const { Title } = Typography;

// Definizione delle costanti di colore (adattate al tema esistente)
const PRIMARY_300 = '#5E77AB'; // Utilizzo del colore principale direttamente
const PRIMARY_200 = '#7891C0'; // Versione più chiara del colore primario
const PRIMARY_100 = '#A5B4D6'; // Versione ancora più chiara per il gradiente
const PRIMARY_50 = '#D2DBE9'; // Versione molto chiara, quasi bianca
const WHITE = '#FFFFFF';

// Funzione per ottenere il testo del tooltip in base al tipo di card
const getTooltipText = (activeTab) => {
    if (!activeTab) return "Data trend over time";

    switch (activeTab) {
        case 'USER':
        case 'Users':
        case 'USERS':
            return "Unique users trend over the selected period";
        case 'SESSION':
        case 'Sessions':
        case 'SESSIONS':
            return "Sessions trend over the selected period";
        case 'NEW USER':
        case 'New Users':
        case 'NEW USERS':
            return "New users trend over the selected period";
        case 'AVG TIME':
        case 'Avg Time':
            return "Average session time trend over the selected period";
        case 'AVG ENG TIME':
        case 'Avg Eng Time':
            return "Average engagement time trend over the selected period";
        case 'AVG S/U':
            return "Average sessions per user trend";
        default:
            return "Data trend over time";
    }
};

// Funzione per convertire i secondi in minuti con decimali
const convertiSecondiAMinuti = (secondi) => {
    if (!secondi || isNaN(secondi)) return "0:00";
    const minuti = Math.floor(secondi / 60);
    const secondiRimanenti = Math.floor(secondi % 60);
    return `${minuti}:${secondiRimanenti.toString().padStart(2, '0')}`;
};

// Componente per l'intestazione del grafico
const ChartHeader = ({ title, activeTab, toolTipText }) => {
    // Il titolo include dinamicamente l'activeTab se disponibile, altrimenti usa il titolo fornito
    const displayTitle = activeTab ? activeTab.toUpperCase() : title.toUpperCase();
    const overviewText = activeTab ? " - Overview" : "";
    // Usa il toolTipText passato dal parent o genera uno di fallback
    const tooltipText = toolTipText || getTooltipText(activeTab);

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'clamp(0.5rem, 2vw, 1rem)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Title level={4} style={{ margin: 0, fontSize: 'clamp(1rem, 2.5vw, 1.5rem)' }}>
                    {displayTitle}
                    <span style={{ fontWeight: 300 }}>{overviewText}</span>
                </Title>
                <CustomTooltip title={tooltipText}>
                    <InfoCircleOutlined style={{
                        marginLeft: 'clamp(0.25rem, 1vw, 0.5rem)',
                        fontSize: 'clamp(0.875rem, 1.5vw, 1.125rem)',
                        color: PRIMARY_300
                    }} />
                </CustomTooltip>
            </div>
        </div>
    );
};

// Componente personalizzato per il tooltip
const CustomLineTooltip = ({ point }) => {
    // Usando lo stesso stile del tooltip di DeviceAnalytics.js
    const tooltipStyle = {
        backgroundColor: 'white',
        padding: '8px 12px',
        border: '1px solid #e8e8e8',
        borderRadius: '2px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    };

    // Formattazione del valore con cifre appropriate o formato tempo
    let formattedValue;

    if (point.serieId === 'Avg Time' || point.serieId === 'Avg Eng Time') {
        // Formatta come tempo (minuti:secondi)
        formattedValue = convertiSecondiAMinuti(point.data.y);
    } else if (typeof point.data.y === 'number') {
        // Per valori interi, non mostrare decimali; per decimali, mostra solo se necessario
        if (Number.isInteger(point.data.y)) {
            formattedValue = point.data.y.toString();
        } else {
            formattedValue = point.data.y.toFixed(2);
        }
    } else {
        // Usa il valore così com'è
        formattedValue = point.data.y;
    }

    return (
        <div style={tooltipStyle}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ fontWeight: 'bold' }}>Date:</span>
                <span style={{ marginLeft: '4px' }}>{point.data.x}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                <span style={{ fontWeight: 'bold' }}>Value:</span>
                <span style={{ marginLeft: '4px' }}>{formattedValue}</span>
            </div>
        </div>
    );
};

function SummaryGraphs({ data, isLoading, title = "Data Overview", activeTab, toolTipText }) {

    // Se non ci sono dati o etichette, mostra l'elemento vuoto
    if (!data || !data.labels || data.labels.length === 0) {
        return <EmptyCard />;
    }

    // Preparazione dei dati per il grafico Nivo
    const nivoData = [
        {
            id: data.datasets[0]?.label || "Value",
            color: PRIMARY_300,
            data: data.labels.map((label, index) => ({
                x: label,
                y: data.datasets[0].data[index]
            }))
        }
    ];

    // Definizione del gradiente per l'area sotto la linea
    const areaGradient = [
        {
            id: 'areaGradient',
            type: 'linearGradient',
            colors: [
                { offset: 0, color: `${PRIMARY_200}90` },     // PRIMARY_200 con 56% opacità
                { offset: 25, color: `${PRIMARY_100}70` },    // PRIMARY_100 con 44% opacità  
                { offset: 50, color: `${PRIMARY_100}50` },    // PRIMARY_100 con 31% opacità
                { offset: 75, color: `${PRIMARY_50}30` },     // PRIMARY_50 con 19% opacità
                { offset: 100, color: `${WHITE}10` },         // Quasi bianco con 6% opacità
            ],
        }
    ];

    // Applicazione del gradiente all'area
    const fills = [
        { match: '*', id: 'areaGradient' }
    ];

    // Funzione per ottimizzare le etichette dell'asse x
    const optimizeXAxisTicks = (values) => {
        // Se ci sono pochi valori, mostrarli tutti
        if (values.length <= 6) return values;

        const result = [];
        const step = Math.ceil(values.length / 6); // Mostriamo circa 6 label

        for (let i = 0; i < values.length; i += step) {
            result.push(values[i]);
        }

        // Assicuriamoci di includere l'ultimo valore
        if (result[result.length - 1] !== values[values.length - 1]) {
            result.push(values[values.length - 1]);
        }

        return result;
    };

    return (
        <Card>
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <ChartHeader title={title} activeTab={activeTab} toolTipText={toolTipText} />
                <div style={{ flexGrow: 1, position: 'relative', height: '320px' }}>
                    {isLoading ? (
                        <LoadingCard
                            height="100%"
                            padding="0"
                            style={{ border: 'none', boxShadow: 'none' }}
                        />
                    ) : (
                        <ResponsiveLine
                            data={nivoData}
                            margin={{ top: 20, right: 10, bottom: 40, left: 35 }}
                            xScale={{ type: 'point' }}
                            yScale={{
                                type: 'linear',
                                min: 'auto',
                                max: 'auto',
                                stacked: false,
                                reverse: false
                            }}
                            curve="monotoneX"
                            axisTop={null}
                            axisRight={null}
                            axisBottom={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                                legend: "",
                                legendOffset: 36,
                                legendPosition: 'middle',
                                format: (value) => {
                                    // Semplificare ulteriormente
                                    const parts = value.split('/');
                                    if (parts.length > 1) {
                                        return parts[0] + '/' + parts[1].substring(0, 3); // Es: 20/Feb
                                    }
                                    return value;
                                },
                                tickValues: optimizeXAxisTicks(data.labels)
                            }}
                            axisLeft={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                                legend: "",
                                legendOffset: -40,
                                legendPosition: 'middle',
                                tickValues: 3,
                                format: (value) => Math.round(value)
                            }}
                            enableGridX={false}
                            enableGridY={true}
                            gridYValues={3}
                            colors={[PRIMARY_300]}
                            lineWidth={2}
                            enablePoints={true}
                            pointSize={8}
                            pointColor={PRIMARY_300}
                            pointBorderWidth={0}
                            pointBorderColor={WHITE}
                            enableArea={true}
                            areaOpacity={1}
                            areaBaselineValue={0}
                            defs={areaGradient}
                            fill={fills}
                            useMesh={true}
                            legends={[]}
                            theme={{
                                axis: {
                                    ticks: {
                                        text: {
                                            fontSize: 11,
                                            fill: '#666'
                                        }
                                    }
                                },
                                grid: {
                                    line: {
                                        stroke: '#eee',
                                        strokeWidth: 1
                                    }
                                }
                            }}
                            tooltip={CustomLineTooltip}
                            animate={true}
                            motionConfig="gentle"
                            enableSlices={false}
                        />
                    )}
                </div>
            </div>
        </Card>
    );
};

export default SummaryGraphs;